import { Grid, Typography } from '@mui/material';
import { useEffect, useId, useState } from 'react';
import {
  useGetInventoryOptions,
  useGetPropertyTypes,
  useGetRealEstateUseTypes,
} from '../../../../../hooks/data';
import {
  isLiabilitiesAssetCategory,
  isOtherAssetCategory,
  isPersonalPropertyAssetCategory,
} from '../../../../../Interfaces/Asset/AssetCategoryTypes';
import {
  IAsset,
  updateOrCreateAsset,
} from '../../../../../Interfaces/Asset/IAsset';
import { IAssetCategoryId } from '../../../../../Interfaces/Asset/IAssetCategory';
import { IAssetTypeId } from '../../../../../Interfaces/Asset/IAssetType';
import { IConfig } from '../../../../../Interfaces/IConfig';
import { ILegalClient } from '../../../../../Interfaces/ILegalClient';
import { ILegalTransactionRelationship } from '../../../../../Interfaces/ILegalTransactionRelationship/ILegalTransactionRelationship';
import { LegalTransactionRelationshipRoleEnum } from '../../../../../Interfaces/ILegalTransactionRelationship/LegalTransactionRelationshipRoleEnum';
import { isInheritanceProvisionRelationship } from '../../../../../Interfaces/ILegalTransactionRelationship/Relationships/InheritanceProvision/ILegalTransactionRelationshipInheritanceProvision';
import { IRealEstate } from '../../../../../Interfaces/IRealEstate';
import { IRealEstateHasInventoryOption } from '../../../../../Interfaces/IRealEstateHasInventoryOption';
import { IRealEstateHasPropertyType } from '../../../../../Interfaces/IRealEstateHasPropertyType';
import { CustomeSlide } from '../../../../core/CustomeSlide';
import { useDialogContext } from '../../../../core/DialogPresenter/DialogContext';
import { useRegisterDialogPage } from '../../../../core/DialogPresenter/useRegisterDialogPage';
import { useLegalClientStore } from '../../generic/legalClientStore';
import { NavRow } from '../../generic/NavRow';
import {
  isTestamentRelationship,
  nil,
  updateOrCreateRelationship,
} from '../../generic/utilities';
import {
  ITestamentAssetOwnerAndProvisions,
  TestamentAssetsDialog,
} from '../components/TestamentAssetsDialog';
import { LiabilityAssetsSection } from '../components/TestamentAssetSections/LiabilityAssetsSection';
import { OtherAssetsSection } from '../components/TestamentAssetSections/OtherAssetsSection';
import { PersonalPropertyAssetsSection } from '../components/TestamentAssetSections/PersonalPropertyAssetsSection';
import { RealEstateAssetsSection } from '../components/TestamentAssetSections/RealEstateAssetsSection';
import testamentAssetsDataService from '../services/testamentAssetsDataService';

export type ITestamentAssetsProps = {
  configObject: IConfig;
  relationshipArray: ILegalTransactionRelationship[];
  setRelationshipArray: React.Dispatch<
    React.SetStateAction<ILegalTransactionRelationship[]>
  >;
  assetArray: IAsset[];
  setAssetArray: React.Dispatch<React.SetStateAction<IAsset[]>>;
};

export function TestamentAssets({
  configObject,
  relationshipArray,
  setRelationshipArray,
  assetArray,
  setAssetArray,
}: ITestamentAssetsProps) {
  const id = useId();
  const { handleNext, handleBack } = useDialogContext();

  const pageNumber = useRegisterDialogPage(id, {
    title: 'Vermögenswerte',
  });

  const [legalClientArray, putLegalClient] = useLegalClientStore(state => [
    state.legalClientArray,
    state.putLegalClient,
  ]);

  const [setLegalClientArray] = useState<
    React.Dispatch<React.SetStateAction<ILegalClient[]>>
  >(() => putLegalClient);

  const [idRealEstate, setIdRealEstate] = useState<number | null>(null);
  const [realEstateArray, setRealEstateArray] = useState<IRealEstate[]>([]);
  const [realEstateHasPropertyTypeArray, setRealEstateHasPropertyTypeArray] =
    useState<IRealEstateHasPropertyType[]>([]);
  const [
    realEstateHasInventroyOptionArray,
    setRealEstateHasInventroyOptionArray,
  ] = useState<IRealEstateHasInventoryOption[]>([]);
  const { propertyTypes, isLoadingPropertyTypes } = useGetPropertyTypes();
  const { inventoryOptions, isLoadingInventoryOptions } =
    useGetInventoryOptions();
  const { realEstateUseTypes, isLoadingRealEstateUseTypes } =
    useGetRealEstateUseTypes();

  const [currentAssetCategoryId, setCurrentAssetCategoryId] =
    useState<IAssetCategoryId | null>(null);
  const [currentAssetTypeId, setCurrentAssetTypeId] =
    useState<IAssetTypeId | null>(null);
  const [currentAssetId, setCurrentAssetId] = useState<number | null>(null);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const newRealEstates = realEstateArray.filter(
      re =>
        !assetArray.some(
          asset =>
            asset.idAssetType === IAssetTypeId.RealEstate &&
            asset.data.idRealEstate === re.idRealEstate
        )
    );
    const assetsWithUpdatedRealEstates = assetArray.map(asset => {
      if (asset.idAssetType === IAssetTypeId.RealEstate) {
        const updatedRealEstate = realEstateArray.find(
          re => re.idRealEstate === asset.data.idRealEstate
        );
        if (updatedRealEstate) {
          return {
            ...asset,
            data: updatedRealEstate,
          };
        }
      }
      return asset;
    });
    const assetsWithNewRealEstates: IAsset[] = newRealEstates.map(re => ({
      idAsset: Date.now() + Math.random(),
      idAssetType: IAssetTypeId.RealEstate,
      idAssetCategory: IAssetCategoryId.RealEstate,
      data: re,
    }));
    setAssetArray([
      ...assetsWithUpdatedRealEstates,
      ...assetsWithNewRealEstates,
    ]);
  }, [realEstateArray]);

  const handleOpen = (
    assetCategoryId: IAssetCategoryId,
    assetTypeId?: IAssetTypeId,
    assetId?: number
  ) => {
    setCurrentAssetCategoryId(assetCategoryId);
    setCurrentAssetTypeId(assetTypeId || null);
    setCurrentAssetId(assetId || null);
    setOpen(true);
  };

  const handleDeleteAssetFromAssets = (assetId: number) => {
    setAssetArray(prevAssets =>
      prevAssets.filter(asset => asset.idAsset !== assetId)
    );
  };

  const handleDeleteAssetFromLegalClients = (assetId: number) => {
    legalClientArray.forEach(legalClient => {
      const hasAsset = legalClient.AssetArray.some(
        la => la.idAsset === assetId
      );
      if (hasAsset) {
        const updatedLegalClient: ILegalClient = {
          ...legalClient,
          AssetArray: legalClient.AssetArray.filter(
            la => la.idAsset !== assetId
          ),
        };
        putLegalClient(updatedLegalClient);
      }
    });
  };

  const handleSaveAssetsDialog = (
    testamentAssetOwnerAndProvisions: ITestamentAssetOwnerAndProvisions
  ) => {
    setRelationshipArray(
      testamentAssetsDataService
        .handleUpdateRelationships({
          ...testamentAssetOwnerAndProvisions,
          relationshipArray,
        })
        .reduce(
          (acc, rel) => updateOrCreateRelationship(acc, rel),
          relationshipArray
        )
    );
    setAssetArray(
      updateOrCreateAsset(assetArray, testamentAssetOwnerAndProvisions.asset)
    );
    testamentAssetsDataService
      .handleUpdateLegalClients(testamentAssetOwnerAndProvisions)
      .forEach(legalClient => putLegalClient(legalClient));

    setOpen(false);
  };

  const handleDeleteAssetFromRelationships = (assetId: number) => {
    const updatedRelationships = relationshipArray.filter(rel => {
      if (
        isTestamentRelationship(rel) ||
        isInheritanceProvisionRelationship(rel)
      ) {
        return !rel.AssetArray.some(a => a.idAsset === assetId);
      }
      return true;
    });
    setRelationshipArray(updatedRelationships);
  };

  const handleDelete = (assetId: number) => {
    handleDeleteAssetFromAssets(assetId);
    handleDeleteAssetFromLegalClients(assetId);
    handleDeleteAssetFromRelationships(assetId);
  };

  const testamentRelationship = relationshipArray.find(isTestamentRelationship);
  const testators = testamentRelationship?.Members.filter(
    member =>
      member.idRelationshipRole ===
      LegalTransactionRelationshipRoleEnum.Testator
  );

  console.log('TestamentAssets rendered with testators:', testators);

  if (nil(pageNumber)) {
    return null;
  }

  if (
    isLoadingPropertyTypes ||
    isLoadingInventoryOptions ||
    isLoadingRealEstateUseTypes ||
    nil(testators)
  ) {
    return (
      <CustomeSlide key={id} pageNumber={pageNumber} appear={false}>
        <Typography>Loading...yomama</Typography>;
      </CustomeSlide>
    );
  }

  if (!propertyTypes || !inventoryOptions || !realEstateUseTypes) {
    return (
      <CustomeSlide key={id} pageNumber={pageNumber} appear={false}>
        <Typography>Fehler beim Laden der Daten.</Typography>;
      </CustomeSlide>
    );
  }

  console.log(
    'Rendering TestamentAssets with relationshipArray:',
    relationshipArray
  );
  console.log('Rendering TestamentAssets with assetArray:', assetArray);

  return (
    <CustomeSlide key={id} pageNumber={pageNumber} appear={false}>
      <Grid container spacing={2}>
        <Grid item xs={12} sx={{ mt: 5 }} />

        <Grid item xs={12}>
          <Typography variant='h4' sx={{ mb: 3 }}>
            Vermögenswerte
          </Typography>
        </Grid>

        <Grid item xs={12}>
          <Typography>
            In diesem Abschnitt können Sie die zu vererbenden Vermögenswerte des
            Erblassers angeben. Diese Informationen sind wichtig für die
            rechtliche Gültigkeit des Testaments.
          </Typography>
        </Grid>

        <PersonalPropertyAssetsSection
          assets={assetArray.filter(isPersonalPropertyAssetCategory)}
          handleOpen={handleOpen}
          handleDelete={handleDelete}
        />

        <RealEstateAssetsSection
          configObject={configObject}
          handleBack={handleBack}
          handleNext={handleNext}
          realEstateArray={realEstateArray}
          setRealEstateArray={setRealEstateArray}
          realEstateHasPropertyTypeArray={realEstateHasPropertyTypeArray}
          setRealEstateHasPropertyTypeArray={setRealEstateHasPropertyTypeArray}
          propertyTypeArray={propertyTypes}
          realEstateHasInventroyOptionArray={realEstateHasInventroyOptionArray}
          setRealEstateHasInventroyOptionArray={
            setRealEstateHasInventroyOptionArray
          }
          inventoryOptionArray={inventoryOptions}
          realEstateUseTypeArray={realEstateUseTypes}
          legalClientArray={legalClientArray}
          setLegalClientArray={
            setLegalClientArray as React.Dispatch<
              React.SetStateAction<ILegalClient[]>
            >
          }
          open={false}
          setOpen={setOpen}
          assets={assetArray}
          setAssets={setAssetArray}
        />

        <LiabilityAssetsSection
          assets={assetArray.filter(isLiabilitiesAssetCategory)}
          handleOpen={handleOpen}
          handleDelete={handleDelete}
        />

        <OtherAssetsSection
          assets={assetArray.filter(isOtherAssetCategory)}
          handleOpen={handleOpen}
          handleDelete={handleDelete}
        />

        {open && (
          <TestamentAssetsDialog
            relationshipArray={relationshipArray}
            assets={assetArray}
            onClose={() => setOpen(false)}
            testators={testators}
            realEstates={assetArray.filter(
              asset => asset.idAssetType === IAssetTypeId.RealEstate
            )}
            onSave={handleSaveAssetsDialog}
            assetCategoryId={currentAssetCategoryId}
            initialAssetTypeId={currentAssetTypeId}
            assetId={currentAssetId}
          />
        )}

        <NavRow handleBack={handleBack} handleNext={handleNext} />
      </Grid>
    </CustomeSlide>
  );
}
